import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  Package, Truck, CheckCircle2, Clock, 
  MapPin, ChevronLeft, Box, ShieldCheck, AlertTriangle, XCircle, Home, Search, Calendar, CreditCard
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion"; 
// 🔥 SEO Component Import
import SEO from "../components/SEO";

const TrackOrder = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const token = user?.accessToken || user?.token;
        const res = await axios.get(`https://wethrift-backend.onrender.com/api/orders/find/${user._id}`, {
          headers: { token: `Bearer ${token}` }
        });
        const currentOrder = res.data.find(o => o._id === id);
        setOrder(currentOrder);
      } catch (err) {
        console.error("Tracking Error:", err);
      } finally {
        setLoading(false);
      }
    };
    if(user?._id) fetchOrderDetails();
  }, [id, user]);

  const steps = [
    { label: "Order Confirmed", status: "pending", icon: <Package size={22} />, desc: "Manifest received by central command" },
    { label: "Quality Check", status: "processing", icon: <CheckCircle2 size={22} />, desc: "Asset verification in progress" },
    { label: "Dispatched", status: "shipped", icon: <Truck size={22} />, desc: "Asset in transit to delivery node" },
    { label: "Delivered", status: "delivered", icon: <Home size={22} />, desc: "Deployment successfully completed" },
  ];

  const currentStatusIndex = steps.findIndex(s => s.status === order?.status?.toLowerCase());
  const isCancelled = order?.status?.toLowerCase() === "cancelled";

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black gap-4 font-mono">
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
        className="w-12 h-12 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full"
      />
      <p className="text-zinc-500 text-[10px] font-[1000] uppercase tracking-[0.5em] animate-pulse">Syncing_Database...</p>
    </div>
  );

  if (!order) return <div className="min-h-screen flex items-center justify-center bg-black text-white italic uppercase font-black tracking-widest">Archive_Not_Found</div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-28 pb-20 px-4 font-sans selection:bg-emerald-500 selection:text-black overflow-x-hidden">
      
      {/* 🚀 Dynamic SEO for Tracking Page */}
      <SEO 
        title={`Track Order #${order.orderNumber || id.slice(-6).toUpperCase()}`} 
        description={`Live tracking for your acquisition at THE_ARCHIVE. Current Status: ${order.status.toUpperCase()}. Track your rare thrifted assets in real-time.`}
        url={`/track/${id}`}
      />

      {/* Background Decorative HUD */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-20 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-500/10 blur-[150px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-blue-500/10 blur-[150px] rounded-full" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        
        <Link to="/profile" className="inline-flex items-center gap-2 text-zinc-600 hover:text-emerald-500 transition-all text-[10px] font-black uppercase tracking-widest mb-10 group bg-white/5 px-4 py-2 rounded-full border border-white/5">
          <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back_To_Profile
        </Link>

        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-white/5 pb-10 mb-12 gap-6"
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
               <span className="bg-emerald-500 text-black text-[8px] font-black px-2 py-0.5 rounded uppercase">Live_Feed</span>
               <p className="text-zinc-500 text-[10px] font-black uppercase tracking-[0.4em] italic">
                 Order_Ref: {order.orderNumber || `WTH-${order._id.slice(-6).toUpperCase()}`}
               </p>
            </div>
            <h1 className={`text-6xl md:text-8xl font-[1000] italic tracking-tighter uppercase leading-none ${isCancelled ? 'text-red-600' : 'text-white'}`}>
              {isCancelled ? "Terminated" : "Live_Status"}
            </h1>
          </div>
          <div className="flex flex-col md:items-end gap-2">
            <div className="bg-white/5 p-4 md:p-6 rounded-[2rem] border border-white/5 backdrop-blur-md">
              <p className="text-zinc-500 text-[9px] font-black uppercase tracking-widest italic mb-1">Settlement_Value</p>
              <p className="text-4xl font-black italic tracking-tighter text-emerald-500">₹{order.amount.toLocaleString()}</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          
          {/* LEFT: Tracking Timeline */}
          <div className="space-y-8">
            {isCancelled ? (
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-red-500/5 border border-red-500/20 rounded-[3rem] p-10 relative overflow-hidden">
                 <XCircle size={150} className="absolute -right-10 -bottom-10 text-red-500 opacity-5" />
                 <div className="relative z-10">
                    <div className="flex items-center gap-4 text-red-500 mb-6">
                      <AlertTriangle size={32} />
                      <h2 className="text-2xl font-black italic uppercase tracking-tighter">System_Termination</h2>
                    </div>
                    <div className="bg-black/40 border border-white/10 p-6 rounded-2xl">
                      <p className="text-zinc-400 text-sm font-bold italic leading-relaxed uppercase">
                        Reason: {order.cancellationReason || "The administrator has terminated this protocol without a specific log entry."}
                      </p>
                    </div>
                 </div>
              </motion.div>
            ) : (
              <div className="bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/5 rounded-[3.5rem] p-10 md:p-14 relative shadow-2xl">
                <div className="relative space-y-16">
                  <div className="absolute left-[27px] top-2 bottom-2 w-[2px] bg-zinc-800/50">
                    <motion.div 
                      initial={{ height: 0 }}
                      animate={{ height: `${currentStatusIndex === -1 ? 0 : (currentStatusIndex / (steps.length - 1)) * 100}%` }}
                      transition={{ duration: 1.5, ease: "circOut" }}
                      className="w-full bg-emerald-500 shadow-[0_0_25px_rgba(16,185,129,1)]" 
                    />
                  </div>

                  {steps.map((step, index) => {
                    const isActive = index <= currentStatusIndex;
                    const isCurrent = index === currentStatusIndex;
                    
                    return (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: isActive ? 1 : 0.2, x: 0 }}
                        transition={{ delay: index * 0.2 }}
                        key={index} 
                        className={`relative flex items-start gap-8 transition-all duration-700`}
                      >
                        <div className={`w-14 h-14 rounded-[1.2rem] flex items-center justify-center z-10 border-2 transition-all duration-700 flex-shrink-0 ${isActive ? "bg-emerald-500 border-emerald-400 text-black scale-110 shadow-[0_0_30px_rgba(16,185,129,0.4)]" : "bg-zinc-900 border-zinc-800 text-zinc-600"}`}>
                          {step.icon}
                        </div>
                        <div className="pt-1">
                          <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${isActive ? "text-emerald-500" : "text-zinc-600"}`}>Phase_0{index + 1}</p>
                          <h4 className={`text-xl font-black uppercase italic tracking-tight ${isActive ? "text-white" : "text-zinc-800"}`}>{step.label}</h4>
                          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-tight mt-1 opacity-60">{step.desc}</p>
                          {isCurrent && (
                            <motion.div 
                              animate={{ opacity: [0.4, 1, 0.4] }}
                              transition={{ repeat: Infinity, duration: 2 }}
                              className="inline-flex items-center gap-2 mt-3 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg"
                            >
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></span>
                                <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest italic">Live_Sync_Active</p>
                            </motion.div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
               {order.products?.map((p, i) => (
                 <div key={i} className="flex items-center gap-4 bg-white/5 border border-white/5 p-4 rounded-3xl hover:bg-white/10 transition-all group">
                    <div className="w-20 h-24 rounded-2xl overflow-hidden bg-zinc-800 flex-shrink-0">
                      <img src={p.productId?.img || p.img} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" crossOrigin="anonymous" />
                    </div>
                    <div className="overflow-hidden">
                       <p className="text-[8px] font-black text-emerald-500 uppercase mb-1">{p.productId?.brand || "Archive_Asset"}</p>
                       <h5 className="text-[11px] font-[1000] uppercase italic text-white truncate">{p.productId?.name || p.name}</h5>
                       <div className="flex gap-4 mt-2">
                          <p className="text-[9px] font-black text-zinc-500 uppercase italic">Size: {p.size || "OS"}</p>
                          <p className="text-[9px] font-black text-zinc-500 uppercase italic">Qty: {p.quantity || 1}</p>
                       </div>
                    </div>
                 </div>
               ))}
            </div>
          </div>

          {/* RIGHT: System Intelligence Panels */}
          <div className="space-y-6">
            {!isCancelled && (
              <div className="bg-emerald-500 text-black rounded-[2.5rem] p-8 shadow-[0_20px_40px_rgba(16,185,129,0.2)]">
                <div className="flex items-center gap-3 mb-6">
                   <Clock size={20} className="animate-pulse" />
                   <h4 className="text-[10px] font-[1000] uppercase tracking-widest">Estimated_Deployment</h4>
                </div>
                <p className="text-4xl font-[1000] italic tracking-tighter uppercase leading-none mb-1">
                   {order.status === "delivered" ? "Completed" : "Next 3-4 Days"}
                </p>
                <p className="text-[9px] font-black uppercase tracking-widest opacity-60">Calculated by Archive_AI</p>
              </div>
            )}

            <div className="bg-zinc-950 border border-white/5 rounded-[2.5rem] p-8 relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 opacity-[0.03] text-white group-hover:rotate-12 transition-transform duration-700">
                <MapPin size={120} />
              </div>
              <h4 className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-6 italic">
                <MapPin size={14} className="text-emerald-500" /> Destination_Node
              </h4>
              <p className="text-zinc-200 font-bold leading-relaxed text-xs uppercase tracking-wide italic border-l border-emerald-500/30 pl-4">
                {order.address}
              </p>
            </div>
            
            <div className="bg-zinc-950 border border-white/5 rounded-[2.5rem] p-8">
              <h4 className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-8 italic">
                <ShieldCheck size={14} className="text-emerald-500" /> Security_Registry
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <div className="flex items-center gap-2">
                    <CreditCard size={12} className="text-zinc-600" />
                    <span className="text-[9px] font-black text-zinc-600 uppercase italic">Method</span>
                  </div>
                  <span className="text-[10px] font-black text-zinc-200 uppercase tracking-tighter italic">{order.payment_method || "GATEWAY_AUTH"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Calendar size={12} className="text-zinc-600" />
                    <span className="text-[9px] font-black text-zinc-600 uppercase italic">Date</span>
                  </div>
                  <span className="text-[10px] font-black text-zinc-200 uppercase tracking-tighter italic">
                    {new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
              <div className={`mt-8 py-4 rounded-2xl text-center border transition-all ${order.isPaid ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-500' : 'bg-zinc-900 border-white/5 text-zinc-500'}`}>
                <p className="text-[10px] font-black uppercase tracking-[0.2em]">
                  {order.isPaid ? 'Payment_Verified' : 'Payment_Pending'}
                </p>
              </div>
            </div>

            <div className="p-8 rounded-[2.5rem] bg-white/5 border border-dashed border-white/10 text-center">
              <p className="text-[9px] font-black text-zinc-500 uppercase tracking-widest mb-4 italic">
                Need technical assistance?
              </p>
              <a 
                href={`https://wa.me/917900086873?text=Hi, I need support for my Order ID: ${id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-500 underline text-[10px] font-black uppercase tracking-widest hover:text-white transition-colors"
              >
                OPEN_SUPPORT_TICKET
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;